import { StatusBar } from 'expo-status-bar';
import React, { useState } from 'react';
import { Text, View } from 'react-native';
import { NativeBaseProvider, Button } from 'native-base';
import { TextInput, SegmentedButtons } from 'react-native-paper';
import { Formik } from 'formik';
import { useMutation } from '@apollo/client';
import { useToast } from 'react-native-toast-notifications';
import { useNavigation } from '@react-navigation/native';
import * as Yup from 'yup';
import { InviteMutation, CREATE_CHILD_ACCOUNT } from '../../gql/user/mutation';
import { styles } from '../Common.style';
import { SCREEN_NAMES } from '../../utils/constant';
import { AllUsersQuery } from '../../gql/user/query';

const regularSchema = Yup.object().shape({
  firstName: Yup.string().required('Requis'),
  lastName: Yup.string().required('Requis'),
  email: Yup.string().email('Email invalide').required('Requis'),
});

const childSchema = Yup.object().shape({
  firstName: Yup.string().required('Requis'),
  lastName: Yup.string().required('Requis'),
  dateOfBirth: Yup.string().required('Requis'),
  parentEmail: Yup.string().email('Email invalide').required('Requis'),
});

function Invite() {
  const toast = useToast();
  const navigation = useNavigation();
  const [accountType, setAccountType] = useState('regular');
  const isChild = accountType === 'child';

  const refetchQueries = [{ query: AllUsersQuery }, 'AllUsers'];

  const [invite, { error: inviteError }] = useMutation(InviteMutation, {
    onCompleted: (data) => {
      toast.show(data?.invite?.message);
      navigation.navigate(SCREEN_NAMES.USERMANAGEMENT);
    },
    refetchQueries,
  });

  const [createChildAccount, { error: childError }] = useMutation(CREATE_CHILD_ACCOUNT, {
    onCompleted: (data) => {
      toast.show(data?.createChildAccount?.message);
      navigation.navigate(SCREEN_NAMES.USERMANAGEMENT);
    },
    refetchQueries,
  });

  if (inviteError) toast.show(inviteError.message);
  if (childError) toast.show(childError.message);

  const handleSubmit = (values) => {
    if (isChild) {
      createChildAccount({
        variables: {
          firstName: values.firstName,
          lastName: values.lastName,
          dateOfBirth: values.dateOfBirth,
          parentEmail: values.parentEmail,
        },
      });
    } else {
      invite({
        variables: {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
        },
      });
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.Middle}>
        <Text style={styles.mainTitle}>Inviter un utilisateur</Text>
      </View>

      <View style={{ paddingHorizontal: 16, paddingBottom: 16 }}>
        <SegmentedButtons
          value={accountType}
          onValueChange={(v) => v && setAccountType(v)}
          buttons={[
            { value: 'regular', label: 'Compte régulier' },
            { value: 'child', label: 'Compte enfant' },
          ]}
        />
      </View>

      <Formik
        key={accountType}
        initialValues={{
          firstName: '', lastName: '', email: '', dateOfBirth: '', parentEmail: '',
        }}
        onSubmit={handleSubmit}
        validationSchema={isChild ? childSchema : regularSchema}
      >
        {({
          handleBlur, handleSubmit: formikSubmit, values, errors, setFieldValue,
        }) => (
          <>
            <View style={styles.buttonStyle}>
              <View style={styles.input}>
                <TextInput
                  label="Prénom"
                  mode="outlined"
                  value={values.firstName}
                  onChangeText={(v) => setFieldValue('firstName', v)}
                  onBlur={handleBlur('firstName')}
                  left={<TextInput.Icon icon="karate" />}
                />
                {!!errors.firstName && <Text style={styles.error}>{errors.firstName}</Text>}
              </View>

              <View style={styles.input}>
                <TextInput
                  label="Nom"
                  mode="outlined"
                  value={values.lastName}
                  onChangeText={(v) => setFieldValue('lastName', v)}
                  onBlur={handleBlur('lastName')}
                  left={<TextInput.Icon icon="mixed-martial-arts" />}
                />
                {!!errors.lastName && <Text style={styles.error}>{errors.lastName}</Text>}
              </View>

              {isChild ? (
                <>
                  <View style={styles.input}>
                    <TextInput
                      label="Date de naissance (AAAA-MM-JJ)"
                      mode="outlined"
                      value={values.dateOfBirth}
                      onChangeText={(v) => setFieldValue('dateOfBirth', v)}
                      onBlur={handleBlur('dateOfBirth')}
                      left={<TextInput.Icon icon="calendar" />}
                      placeholder="ex: 2012-05-14"
                    />
                    {!!errors.dateOfBirth && <Text style={styles.error}>{errors.dateOfBirth}</Text>}
                  </View>

                  <View style={styles.input}>
                    <TextInput
                      label="Email du parent / responsable légal"
                      mode="outlined"
                      value={values.parentEmail}
                      onChangeText={(v) => setFieldValue('parentEmail', v)}
                      onBlur={handleBlur('parentEmail')}
                      left={<TextInput.Icon icon="email" />}
                      keyboardType="email-address"
                      autoCapitalize="none"
                    />
                    {!!errors.parentEmail && <Text style={styles.error}>{errors.parentEmail}</Text>}
                  </View>
                </>
              ) : (
                <View style={styles.input}>
                  <TextInput
                    label="Email"
                    mode="outlined"
                    value={values.email}
                    onChangeText={(v) => setFieldValue('email', v)}
                    onBlur={handleBlur('email')}
                    left={<TextInput.Icon icon="email" />}
                    keyboardType="email-address"
                    autoCapitalize="none"
                  />
                  {!!errors.email && <Text style={styles.error}>{errors.email}</Text>}
                </View>
              )}
            </View>

            <View style={styles.buttonStyle}>
              <Button onPress={formikSubmit} style={styles.buttonDesign}>
                {isChild ? 'Créer le compte enfant' : 'Inviter'}
              </Button>
            </View>
          </>
        )}
      </Formik>

      <StatusBar />
    </View>
  );
}

export default function InviteScreen() {
  return (
    <NativeBaseProvider>
      <Invite />
    </NativeBaseProvider>
  );
}
